import { useState, useEffect } from "react";
import { User, Phone, Tag, AlertCircle } from "lucide-react";
import { toast } from "react-toastify";

const StaffInfoTab = ({ formData, setFormData }) => {
  const [errors, setErrors] = useState({});

  // تبدیل اعداد فارسی و عربی به انگلیسی
  const convertToEnglishDigits = (value) => {
    const persian = ["۰", "۱", "۲", "۳", "۴", "۵", "۶", "۷", "۸", "۹"];
    const arabic = ["٠", "١", "٢", "٣", "٤", "٥", "٦", "٧", "٨", "٩"];

    return value
      .split("")
      .map((char) => {
        const persianIndex = persian.indexOf(char);
        if (persianIndex !== -1) return persianIndex.toString();

        const arabicIndex = arabic.indexOf(char);
        if (arabicIndex !== -1) return arabicIndex.toString();

        return char;
      })
      .join("");
  };

  // تبدیل اعداد انگلیسی به فارسی
  const convertToPersianDigits = (value) => {
    const persian = ["۰", "۱", "۲", "۳", "۴", "۵", "۶", "۷", "۸", "۹"];
    return value.replace(/\d/g, (digit) => persian[parseInt(digit)]);
  };

  // اعتبارسنجی نام فارسی
  const validatePersianName = (name, fieldName = "نام") => {
    if (!name.trim()) {
      return `${fieldName} نمی‌تواند خالی باشد`;
    }

    if (!/^[آ-ی\s]+$/.test(name.trim())) {
      return `${fieldName} فقط باید شامل حروف فارسی باشد`;
    }

    return null;
  };

  // اعتبارسنجی شماره موبایل
  const validatePhone = (phone) => {
    if (!phone.trim()) {
      return "شماره موبایل نمی‌تواند خالی باشد";
    }

    if (!/^09\d{9}$/.test(phone)) {
      return "شماره موبایل نامعتبر است (باید با 09 شروع شود و 11 رقم باشد)";
    }

    return null;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    // مدیریت نام و نام خانوادگی (فقط فارسی)
    if (name === "firstName" || name === "lastName") {
      // فیلتر کردن هر چیزی غیر از حروف فارسی و فاصله
      const persianOnly = value.replace(/[^آ-ی\s]/g, "");

      setFormData((prev) => ({
        ...prev,
        information: {
          ...prev.information,
          [name]: persianOnly,
        },
      }));

      // پاک کردن خطا
      setErrors((prev) => ({ ...prev, [name]: null }));

      return;
    }

    // مدیریت شماره موبایل
    if (name === "phone") {
      let converted = convertToEnglishDigits(value);
      converted = converted.replace(/\D/g, ""); // حذف هر چیزی غیر از عدد
      converted = converted.slice(0, 11); // محدود به 11 رقم

      setFormData((prev) => ({
        ...prev,
        information: {
          ...prev.information,
          phone: converted,
        },
      }));

      // پاک کردن خطا
      setErrors((prev) => ({ ...prev, phone: null }));

      return;
    }

    // مدیریت نام نمایشی (فقط فارسی + اعداد فارسی)
    if (name === "displayName") {
      // حذف حروف انگلیسی
      let persianOnly = value.replace(/[a-zA-Z]/g, "");
      // تبدیل اعداد انگلیسی به فارسی
      persianOnly = convertToPersianDigits(persianOnly);

      setFormData((prev) => ({
        ...prev,
        information: {
          ...prev.information,
          displayName: persianOnly,
        },
      }));

      setErrors((prev) => ({ ...prev, displayName: null }));
    }
  };

  // اعتبارسنجی هنگام خروج از فیلد
  const handleBlur = (e) => {
    const { name, value } = e.target;

    if (name === "firstName") {
      const error = validatePersianName(value, "نام");
      setErrors((prev) => ({ ...prev, firstName: error }));
      if (error) {
        toast.error(error, { className: "z-[10001]" });
      }
    }

    if (name === "lastName") {
      const error = validatePersianName(value, "نام خانوادگی");
      setErrors((prev) => ({ ...prev, lastName: error }));
      if (error) {
        toast.error(error, { className: "z-[10001]" });
      }
    }

    if (name === "phone") {
      const error = validatePhone(value);
      setErrors((prev) => ({ ...prev, phone: error }));
      if (error) {
        toast.error(error, { className: "z-[10001]" });
      }
    }
  };

  const { firstName, lastName, displayName, phone } = formData.information;

  return (
    <div className="space-y-6">
      
      {/* راهنما */}
      <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 flex items-start gap-3">
        <AlertCircle className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-purple-800">
          <p className="font-medium mb-1">نکات مهم:</p>
          <ul className="list-disc list-inside space-y-1 text-purple-700">
            <li>نام و نام خانوادگی فقط باید به فارسی باشد</li>
            <li>شماره موبایل باید با 09 شروع شود و 11 رقم باشد</li>
            <li>نام نمایشی فقط می‌تواند شامل حروف فارسی و اعداد فارسی باشد</li>
          </ul>
        </div>
      </div>

      {/* Grid فرم */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        
        {/* نام */}
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">
            نام <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <div className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">
              <User className="w-4 h-4" />
            </div>
            <input
              type="text"
              name="firstName"
              value={firstName}
              onChange={handleChange}
              onBlur={handleBlur}
              placeholder="مثال: محمد"
              className={`
                w-full pr-10 pl-4 py-3 text-sm bg-slate-50 border rounded-lg
                text-slate-700 placeholder:text-slate-400
                focus:outline-none focus:bg-white transition-all duration-300
                ${
                  errors.firstName
                    ? "border-red-400 focus:border-red-500"
                    : "border-slate-200 focus:border-purple-400"
                }
              `}
            />
          </div>
          {errors.firstName && (
            <p className="text-xs text-red-600 mt-1.5 flex items-center gap-1">
              <AlertCircle className="w-3 h-3" />
              {errors.firstName}
            </p>
          )}
        </div>

        {/* نام خانوادگی */}
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">
            نام خانوادگی <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <div className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">
              <User className="w-4 h-4" />
            </div>
            <input
              type="text"
              name="lastName"
              value={lastName}
              onChange={handleChange}
              onBlur={handleBlur}
              placeholder="مثال: احمدی"
              className={`
                w-full pr-10 pl-4 py-3 text-sm bg-slate-50 border rounded-lg
                text-slate-700 placeholder:text-slate-400
                focus:outline-none focus:bg-white transition-all duration-300
                ${
                  errors.lastName
                    ? "border-red-400 focus:border-red-500"
                    : "border-slate-200 focus:border-purple-400"
                }
              `}
            />
          </div>
          {errors.lastName && (
            <p className="text-xs text-red-600 mt-1.5 flex items-center gap-1">
              <AlertCircle className="w-3 h-3" />
              {errors.lastName}
            </p>
          )}
        </div>

        {/* نام نمایشی */}
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">
            نام نمایشی
          </label>
          <div className="relative">
            <div className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">
              <Tag className="w-4 h-4" />
            </div>
            <input
              type="text"
              name="displayName"
              value={displayName}
              onChange={handleChange}
              placeholder="مثال: استاد محمد"
              className="w-full pr-10 pl-4 py-3 text-sm bg-slate-50 border border-slate-200 rounded-lg text-slate-700 placeholder:text-slate-400 focus:outline-none focus:border-purple-400 focus:bg-white transition-all duration-300"
            />
          </div>
          <p className="text-xs text-slate-500 mt-1.5">
            این نام در لیست رزرو های عادی نمایش داده می‌شود
          </p>
        </div>

        {/* شماره موبایل */}
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">
            شماره موبایل <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <div className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">
              <Phone className="w-4 h-4" />
            </div>
            <input
              type="text"
              name="phone"
              value={phone}
              onChange={handleChange}
              onBlur={handleBlur}
              placeholder="09123456789"
              dir="ltr"
              className={`
                w-full pr-10 pl-4 py-3 text-sm bg-slate-50 border rounded-lg
                text-slate-700 placeholder:text-slate-400 font-mono
                focus:outline-none focus:bg-white transition-all duration-300
                ${
                  errors.phone
                    ? "border-red-400 focus:border-red-500"
                    : "border-slate-200 focus:border-purple-400"
                }
              `}
            />
          </div>
          {errors.phone && (
            <p className="text-xs text-red-600 mt-1.5 flex items-center gap-1">
              <AlertCircle className="w-3 h-3" />
              {errors.phone}
            </p>
          )}
        </div>

      </div>

      {/* پیش‌نمایش */}
      <div className="bg-gradient-to-br from-slate-50 to-purple-50/30 border border-slate-200 rounded-lg p-5">
        <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
          <span>👁️</span>
          پیش‌نمایش اطلاعات
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
          <div>
            <span className="text-slate-500">نام کامل:</span>
            <span className="text-slate-800 font-medium mr-2">
              {firstName || "---"} {lastName || "---"}
            </span>
          </div>
          <div>
            <span className="text-slate-500">نام نمایشی:</span>
            <span className="text-slate-800 font-medium mr-2">
              {displayName || "تعیین نشده"}
            </span>
          </div>
          <div>
            <span className="text-slate-500">شماره موبایل:</span>
            <span className="text-slate-800 font-medium font-mono mr-2" dir="ltr">
              {phone || "---"}
            </span>
          </div>
        </div>
      </div>

    </div>
  );
};

export default StaffInfoTab;